import Copy from "@/components/copy";
import { Button } from "@/components/ui/button";
import { useApplyForReferral } from "@/hooks/use-mutations";
import {
  useGetUserReferralStats,
  useGetUserReferralList,
} from "@/hooks/use-queries";
import { handleShare } from "@/lib/helper";
import { formatAmount } from "@/lib/utils";
import { useUser } from "@/zustand/store";
import {
  ArrowDownToLine,
  Check,
  ChevronRight,
  Info,
  Link2,
  Lock,
  Mail,
  Search,
  Share2,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const timeAgo = (date: string) => {
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)}mo ago`;
  return `${Math.floor(diff / 31536000)}y ago`;
};

const ApplicationStatus = ({ submittedAt }: { submittedAt?: string }) => {
  const submittedLabel = (() => {
    if (!submittedAt) return "Today";
    const submitted = new Date(submittedAt);
    const now = new Date();
    const diffDays = Math.floor(
      (now.setHours(0, 0, 0, 0) - submitted.setHours(0, 0, 0, 0)) / 86400000,
    );
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return new Date(submittedAt).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  })();

  const steps = [
    {
      label: "Application submitted",
      sub: submittedLabel,
      state: "done" as const,
    },
    {
      label: "Review in progress",
      sub: "Our team is evaluating your application",
      state: "active" as const,
    },
    {
      label: "Access granted",
      sub: "Start earning 25% on every referral",
      state: "pending" as const,
    },
  ];

  const cardStyle = {
    background:
      "#F5F7FB",
    borderColor: "#E1E5EB",
  } as const;

  return (
    <div className="relative pb-6">
      {/* Cyan radial glow — top left */}
      <div
        className="pointer-events-none absolute z-0"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #4a93db33 0%, rgba(74, 147, 219, 0) 100%)",
          width: 440,
          height: 396,
          top: -143,
          left: -123,
        }}
      />

      <div className="relative z-10 overflow-x-hidden">
        {/* Pink radial glow — right edge */}
        <div
          className="pointer-events-none absolute inset-x-0 z-0 blur-md"
          style={{
            background:
              "radial-gradient(350px 350px at 100% 50%, rgba(90, 149, 208, 0.15) 0%, rgba(90, 149, 208, 0) 100%)",
            height: 400,
            top: 180,
          }}
        />

        {/* Header */}
        <div className="relative z-10">
          <p className="text-dark-text-300 text-[10px] font-bold tracking-widest uppercase">
            REFERRAL PROGRAM
          </p>
          <h2 className="text-dark-text-400 mt-0.5 text-xl font-bold">
            Application Status
          </h2>
        </div>

        {/* Hero card */}
        <div
          className="relative z-10 mt-4 rounded-2xl border p-5"
          style={{
            background: "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          <div className="flex flex-col items-center text-center">
            {/* Timer icon */}
            <img
              src="/images/gradient-timer.svg"
              alt=""
              className="size-16 object-contain"
            />

            {/* UNDER REVIEW badge */}
            <div
              className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1"
              style={{
                background: "#FFBD4C1A",
                border: "1px solid #FFBD4C40",
              }}
            >
              <Lock className="size-3" style={{ color: "#FFBD4C" }} />
              <span
                className="text-[10px] font-bold tracking-wide"
                style={{ color: "#FFBD4C" }}
              >
                UNDER REVIEW
              </span>
            </div>

            {/* Heading */}
            <h2 className="mt-4 text-[22px] leading-tight font-bold text-dark-text-400">
              Application received
            </h2>

            {/* Description */}
            <p className="mt-2 text-[11px] leading-relaxed text-dark-text-300">
              Thanks for applying. Our team reviews applications within{" "}
              <span className="font-semibold text-dark-text-400">
                2–3 business days
              </span>
              . We&apos;ll email you once your application has been approved.
            </p>
          </div>
        </div>

        {/* What happened next */}
        <div
          className="relative z-10 mt-4 rounded-2xl border p-4"
          style={cardStyle}
        >
          <h3 className="text-base font-semibold text-dark-text-400">
            What happened next
          </h3>

          <div className="mt-4">
            {steps.map((step, i) => (
              <div key={step.label} className="flex gap-3">
                {/* Timeline indicator */}
                <div className="flex flex-col items-center">
                  <div
                    className="flex size-6 shrink-0 items-center justify-center rounded-full"
                    style={{
                      background:
                        step.state === "done"
                          ? "#2bc155"
                          : step.state === "active"
                            ? "#FFBD4C"
                            : "#E1E5EB",
                    }}
                  >
                    {step.state === "done" && (
                      <Check className="size-3 text-white" strokeWidth={2.5} />
                    )}
                    {step.state === "active" && (
                      <div className="size-2 rounded-full bg-white" />
                    )}
                    {step.state === "pending" && (
                      <div className="size-2 rounded-full bg-dark-stroke-3" />
                    )}
                  </div>
                  {i < steps.length - 1 && (
                    <div className="my-1 w-px flex-1 bg-dark-stroke-3" />
                  )}
                </div>

                {/* Text */}
                <div className="pb-4">
                  <p
                    className="text-sm font-semibold"
                    style={{
                      color:
                        step.state === "pending"
                          ? "#A0A0A0"
                          : "#150e1f",
                    }}
                  >
                    {step.label}
                  </p>
                  <p className="mt-0.5 text-[11px] text-dark-text-300">{step.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Need update */}
        <div
          className="relative z-10 mt-3 rounded-2xl border p-4"
          style={cardStyle}
        >
          <div className="flex items-start gap-3">
            <div
              className="flex size-10 shrink-0 items-center justify-center rounded-xl"
              style={{
                background: "#4a93db1f",
                border: "1px solid #4a93db33",
              }}
            >
              <Mail className="size-5" style={{ color: "#4a93db" }} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-dark-text-400">
                Need update on your application?
              </p>
              <p className="mt-1 text-[11px] leading-relaxed text-dark-text-300">
                Email us at{" "}
                <a
                  href="mailto:support@My-payhub.com"
                  className="font-medium"
                  style={{ color: "#4a93db" }}
                >
                  support@My-payhub.com
                </a>{" "}
                within 24 hours and we&apos;ll work it in before review.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotWhitelisted = () => {
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { mutate: applyForReferral, isPending } = useApplyForReferral({
    onSuccess: () => {
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    },
  });

  if (submitted) return <ApplicationStatus />;

  const approvalCriteria = [
    "You have an active My-Pay account in good standing",
    "You have an audience or network you can refer (community, newsletter, social, business)",
    "You agree to our promotion guidelines and won't spam or misrepresent the product",
  ];

  return (
    <div className="relative pb-24">
      {/* Cyan radial glow — top left */}
      <div
        className="pointer-events-none absolute z-0"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #4a93db33 0%, rgba(74, 147, 219, 0) 100%)",
          width: 440,
          height: 396,
          top: -143,
          left: -123,
        }}
      />

      <div className="relative z-10">
        {/* Pink radial glow — right edge */}
        <div
          className="pointer-events-none absolute inset-x-0 z-0"
          style={{
            background:
              "radial-gradient(350px 350px at 100% 50%, rgba(90, 149, 208, 0.15) 0%, rgba(90, 149, 208, 0) 100%)",
            height: 400,
            top: 180,
          }}
        />

        {/* Header */}
        <div className="relative z-10">
          <p className="text-dark-text-300 text-[10px] font-bold tracking-widest uppercase">
            REFERRAL PROGRAM
          </p>
          <h2 className="text-dark-text-400 mt-0.5 text-xl font-bold">
            Join the program
          </h2>
        </div>

        {/* Hero card */}
        <div
          className="relative z-10 mt-4 overflow-hidden rounded-2xl border p-5"
          style={{
            background:
              "linear-gradient(var(--gradient-light-blue))",
            borderColor: "#4a93db26",
          }}
        >
          <div className="relative z-10">
            {/* Not whitelisted badge */}
            <div
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1"
              style={{
                background: "#ff63661a",
                border: "1px solid #ff63664d",
              }}
            >
              <Lock className="size-3" style={{ color: "#ff6366" }} />
              <span
                className="text-[10px] font-bold tracking-wide"
                style={{ color: "#ff6366" }}
              >
                NOT YET WHITELISTED
              </span>
            </div>

            {/* Heading + illustration side by side */}
            <div className="mt-4 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 className="text-[22px] leading-tight font-bold text-white">
                  Earn 25% on every,
                  <br />
                  referral <span style={{ color: "#62D1F3" }}>forever</span>.
                </h2>
                <p className="mt-2 text-[11px] leading-relaxed text-white/60">
                  The My-Pay referral program is invitation-based. Apply
                  below and our team will review your fit within 2–3 business
                  days.
                </p>
              </div>

              {/* Illustration */}
              <img
                src="/images/referral-img.svg"
                alt="Referral illustration"
                className="w-[40%] shrink-0 self-center object-contain"
              />
            </div>

            {/* Stats row — full width below heading + illustration */}
            <svg width="0" height="0" style={{ position: "absolute" }}>
              <defs>
                <linearGradient
                  id="referralStatIconGradient"
                  x1="10%"
                  y1="0%"
                  x2="90%"
                  y2="100%"
                >
                  <stop offset="11.02%" stopColor="#E3F7FF" />
                  <stop offset="93.11%" stopColor="#D3BBF1" />
                </linearGradient>
              </defs>
            </svg>
            <div className="mt-4 flex gap-2">
              {[
                {
                  icon: (
                    <TrendingUp
                      className="size-3.5"
                      style={{ stroke: "url(#referralStatIconGradient)" }}
                    />
                  ),
                  value: "25% earnings",
                  label: "On deposit fees",
                },
                {
                  icon: (
                    <Zap
                      className="size-3.5"
                      style={{ stroke: "url(#referralStatIconGradient)" }}
                    />
                  ),
                  value: "Lifetime",
                  label: "No expiry",
                },
                {
                  icon: (
                    <Users
                      className="size-3.5"
                      style={{ stroke: "url(#referralStatIconGradient)" }}
                    />
                  ),
                  value: "Unlimited",
                  label: "Referrals",
                },
              ].map((stat) => (
                <div
                  key={stat.value}
                  className="flex flex-1 flex-col gap-1 rounded-xl p-2.5"
                  style={{
                    background: "#EBE8F308",
                    border: "1px solid #454C6F",
                  }}
                >
                  {stat.icon}
                  <p className="text-[11px] leading-tight font-semibold text-white">
                    {stat.value}
                  </p>
                  <p className="text-[9px] text-white/50">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Who we approve */}
        <div
          className="mt-4 rounded-2xl border p-4"
          style={{
            background:
              "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          <h3 className="text-base font-semibold text-dark-text-400">Who we approve</h3>
          <div className="mt-3 flex flex-col gap-3">
            {approvalCriteria.map((item) => (
              <div key={item} className="flex items-start gap-2.5">
                <div
                  className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full"
                  style={{
                    background: "#4ed79d26",
                    border: "1px solid #4ed79d4d",
                  }}
                >
                  <Check className="size-3" style={{ color: "#4ed79d" }} />
                </div>
                <p className="text-[12px] leading-relaxed text-dark-text-300">
                  {item}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Terms checkbox */}
        <div
          className="mt-3 rounded-2xl border p-4"
          style={{
            background:
              "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          <label className="flex cursor-pointer items-start gap-3">
            <div className="relative mt-0.5 shrink-0">
              <input
                type="checkbox"
                className="peer sr-only"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
              />
              <div
                className="flex size-5 items-center justify-center rounded"
                style={{
                  background: agreed ? "#62D1F3" : "#F5F7FB",
                  border: agreed ? "none" : "1.5px solid #C5CAD6",
                }}
              >
                {agreed && (
                  <Check className="size-3 text-[#0f1326]" strokeWidth={3} />
                )}
              </div>
            </div>
            <p className="text-[12px] leading-relaxed text-dark-text-300">
              I agree to the{" "}
              <span
                className="cursor-pointer font-medium"
                style={{ color: "#4a93db" }}
              >
                Partner Terms
              </span>{" "}
              and the{" "}
              <span
                className="cursor-pointer font-medium"
                style={{ color: "#4a93db" }}
              >
                Promotion Guidelines
              </span>
              . I won&apos;t misrepresent the product or spam users.
            </p>
          </label>
        </div>

        {/* Disclaimer */}
        <div
          className="mt-3 flex items-start gap-2.5 rounded-2xl border p-4"
          style={{
            background:
              "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          <Info className="mt-0.5 size-3.5 shrink-0 text-dark-text-300" />
          <p className="text-[11px] leading-relaxed text-dark-text-300">
            Approval is at My-Pay&apos;s discretion. We typically respond within
            2–3 business days. Approved partners get instant access to their
            referral dashboard and code.
          </p>
        </div>
      </div>
      {/* end overflow-x-hidden */}

      {/* Apply button — sticky bottom */}
      <div className="sticky right-0 bottom-0 left-0 z-20 px-0 pt-3 pb-6">
        <button
          disabled={!agreed || isPending}
          onClick={() => applyForReferral()}
          className="flex h-[44px] w-full items-center justify-center gap-2 text-sm font-semibold transition-opacity active:opacity-80"
          style={{
            background: "linear-gradient(360deg, #5a95d0 0%, #4a93db 100%)",
            color: "#ffffff",
            opacity: agreed && !isPending ? 1 : 0.4,
            borderRadius: 12,
          }}
        >
          SUBMIT APPLICATION
          <ChevronRight className="size-4" strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
};

const Referral = () => {
  const { user } = useUser();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  // Hoisted above the early returns below: the upstream source declares this
  // after them, which violates react-hooks/rules-of-hooks and fails eslint.
  const [linkCopied, setLinkCopied] = useState(false);
  const limit = 10;

  const canRefer = !!user?.canRefer;
  const { data: statsData } = useGetUserReferralStats(canRefer);
  const { data: listData } = useGetUserReferralList(page, limit, canRefer);

  if (!user?.canRefer) {
    if (user?.hasPendingReferralRequest)
      return (
        <ApplicationStatus
          submittedAt={user.pendingReferralRequest?.createdAt}
        />
      );
    return <NotWhitelisted />;
  }
  const referrals = listData?.referrals ?? [];
  const totalPages = Math.ceil((listData?.total ?? 0) / limit);

  const filtered = referrals.filter((r) => {
    const name = `${r.firstName} ${r.lastName}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const feePercent = user?.referralFeePercent ?? 25;
  const totalEarnings = statsData?.totalEarnings ?? 0;
  const totalWithdrawn = statsData?.totalWithdrawn ?? 0;
  const totalReferrals = statsData?.totalReferrals ?? 0;
  const thisMonthReferrals = statsData?.thisMonthReferrals ?? 0;
  const thisMonthEarnings = statsData?.thisMonthEarnings ?? 0;
  const available = statsData?.available ?? 0;
  const minWithdrawal = statsData?.minWithdrawal ?? 100;
  const canTransfer = statsData?.canWithdraw ?? false;
  const referralCode = statsData?.referralCode ?? user?.id ?? "";
  const progress = Math.min((available / minWithdrawal) * 100, 100);

  const handleCopyLink = () => {
    const url = `${window.location.origin}/signup?ref=${referralCode}`;
    navigator.clipboard.writeText(url);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  return (
    <div className="relative pb-20">
      {/* Cyan radial glow — top left */}
      <div
        className="pointer-events-none absolute z-0"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, #4a93db33 0%, rgba(74, 147, 219, 0) 100%)",
          width: 440,
          height: 396,
          top: -143,
          left: -123,
        }}
      />
      <div className="relative z-10">
        {/* Pink radial glow — centre pinned to right edge, no horizontal overflow */}
        <div
          className="pointer-events-none absolute inset-x-0 z-0"
          style={{
            background:
              "radial-gradient(350px 350px at 100% 50%, rgba(90, 149, 208, 0.15) 0%, rgba(90, 149, 208, 0) 100%)",
            height: 400,
            top: 225,
          }}
        />
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-dark-text-400 text-lg font-semibold">Referrals</h2>
          <button
            onClick={() => handleShare(`signup?ref=${referralCode}`)}
            className="rounded-full p-1.5 transition-colors hover:bg-dark-grey-50"
          >
            <Share2 className="text-dark-text-400 size-5" />
          </button>
        </div>

        {/* Hero Banner */}
        <div
          className="relative mt-4 overflow-hidden rounded-2xl border p-4"
          style={{
            background:
              "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          {/* Side-by-side layout */}
          <div className="relative z-10 flex items-center gap-3">
            {/* Left: text content */}
            <div className="min-w-0 flex-1">
              {/* Badge */}
              <div
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1"
                style={{
                  background:
                    "#D5EAFF",
                }}
              >
                <Zap
                  className="size-3"
                  style={{ fill: "#4a93db", color: "#4a93db" }}
                />
                <span
                  className="text-[9px] font-bold tracking-wide"
                  style={{ color: "#4a93db" }}
                >
                  EARN {feePercent}% ON DEPOSIT FEE
                </span>
              </div>

              {/* Heading */}
              <h2 className="mt-2 text-[22px] leading-tight font-bold text-dark-text-400">
                Invite friends,
                <br />
                earn <span style={{ color: "#4a93db" }}>forever.</span>
              </h2>

              {/* Description */}
              <p className="mt-1.5 text-[11px] leading-relaxed text-dark-text-300">
                Get {feePercent}% of every deposit fee from people you refer.
              </p>

              {/* Referral code + Copy link */}
              <div className="mt-3">
                <p className="text-[10px] text-dark-text-300">Your referral code</p>
                <div className="mt-1.5 flex items-center gap-2">
                  {/* Code box — dashed border */}
                  <div
                    className="flex h-9 flex-1 items-center justify-between gap-2 rounded-lg px-3"
                    style={{
                      border: "1.5px dashed #4a93db66",
                      background: "#F5F7FB",
                    }}
                  >
                    <span className="overflow-hidden text-xs font-semibold text-ellipsis whitespace-nowrap text-dark-text-400">
                      {referralCode}
                    </span>
                    <Copy
                      text={referralCode}
                      icon="/icons/copy2.svg"
                      side="left"
                    />
                  </div>

                  {/* Copy link box */}
                  <button
                    onClick={handleCopyLink}
                    className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-3 text-[11px] font-medium text-dark-text-300 transition-colors hover:text-dark-text-400"
                    style={{
                      border: "1px solid #E1E5EB",
                      background: "#F5F7FB",
                    }}
                  >
                    {linkCopied ? (
                      <Check className="size-3.5" style={{ color: "#4ed79d" }} />
                    ) : (
                      <Link2 className="size-3.5" />
                    )}
                    <span>{linkCopied ? "Copied" : "Copy link"}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right: illustration */}
            <img
              src="/images/referral-img.svg"
              alt="Referral illustration"
              className="w-[42%] shrink-0 self-center object-contain"
            />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          {/* Total Referrals */}
          <div
            className="rounded-xl border p-3.5"
            style={{ background: "#F5F7FB", borderColor: "#E1E5EB" }}
          >
            <div
              className="flex size-8 items-center justify-center rounded-[5px]"
              style={{ background: "#4a93db1f" }}
            >
              <img src="/icons/gradient-users.svg" className="size-4" alt="" />
            </div>
            <p className="mt-3 text-[9px] font-semibold tracking-wider text-dark-text-300 uppercase">
              Total Referrals
            </p>
            <p className="mt-1 text-2xl font-bold text-dark-text-400">
              {totalReferrals}
            </p>
            <p className="mt-0.5 text-[10px] text-dark-text-300">
              +{thisMonthReferrals} this month
            </p>
          </div>

          {/* Total Earnings */}
          <div
            className="rounded-xl border p-3.5"
            style={{ background: "#F5F7FB", borderColor: "#E1E5EB" }}
          >
            <div
              className="flex size-8 items-center justify-center rounded-[5px]"
              style={{ background: "#4a93db1f" }}
            >
              <img src="/icons/gradient-wallet.svg" className="size-4" alt="" />
            </div>
            <p className="mt-3 text-[9px] font-semibold tracking-wider text-dark-text-300 uppercase">
              Total Earnings
            </p>
            <p className="mt-1 text-2xl font-bold text-dark-text-400">
              ${formatAmount(totalEarnings)}
            </p>
            <p className="mt-0.5 text-[10px] text-dark-text-300">
              +${formatAmount(thisMonthEarnings)} this month
            </p>
          </div>

          {/* Available */}
          <div
            className="rounded-xl border p-3.5"
            style={{ background: "#F5F7FB", borderColor: "#4ed79d40" }}
          >
            <div
              className="flex size-8 items-center justify-center rounded-[5px]"
              style={{ background: "#4ed79d33" }}
            >
              <TrendingUp className="size-4" style={{ color: "#4ed79d" }} />
            </div>
            <p className="mt-3 text-[9px] font-semibold tracking-wider text-dark-text-300 uppercase">
              Available
            </p>
            <p className="mt-1 text-2xl font-bold text-dark-text-400">
              ${formatAmount(available)}
            </p>
            <p className="mt-0.5 text-[10px] text-dark-text-300">
              Reach ${minWithdrawal} to withdraw
            </p>
          </div>

          {/* Total Withdrawn */}
          <div
            className="rounded-xl border p-3.5"
            style={{ background: "#F5F7FB", borderColor: "#E1E5EB" }}
          >
            <div
              className="flex size-8 items-center justify-center rounded-[5px]"
              style={{ background: "#4a93db33" }}
            >
              <ArrowDownToLine
                className="size-4"
                style={{ color: "#4a93db" }}
              />
            </div>
            <p className="mt-3 text-[9px] font-semibold tracking-wider text-dark-text-300 uppercase">
              Total Withdrawn
            </p>
            <p className="mt-1 text-2xl font-bold text-dark-text-400">
              ${formatAmount(totalWithdrawn)}
            </p>
            <p className="mt-0.5 text-[10px] text-dark-text-300">—</p>
          </div>
        </div>

        {/* Progress + Transfer Card */}
        <div
          className="mt-3 rounded-xl border p-4"
          style={{ background: "#F5F7FB", borderColor: "#4ed79d40" }}
        >
          {/* Wallet icon */}
          <div
            className="flex size-8 items-center justify-center rounded-[5px]"
            style={{ background: "#4ed79d33" }}
          >
            <img
              src="/icons/gradient-wallet-green.svg"
              className="size-5"
              alt=""
            />
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-dark-grey-200">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: "#2bc155" }}
            />
          </div>

          {/* Labels */}
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-dark-text-300">
              Reach the{" "}
              <span className="font-semibold text-dark-text-400">${minWithdrawal}</span>{" "}
              minimum
            </span>
            <span className="font-medium text-dark-text-300">
              ${formatAmount(available)} / ${minWithdrawal}
            </span>
          </div>

          {/* Transfer Button */}
          <Button
            className="mt-4 w-full gap-2 py-5 text-sm font-semibold text-white transition-opacity"
            style={{
              background: "#2bc155",
              opacity: canTransfer ? 1 : 0.5,
            }}
            disabled={!canTransfer}
          >
            <img
              src="/icons/gradient-wallet-green.svg"
              className="size-4"
              style={{ filter: "brightness(0.2) saturate(2)" }}
              alt=""
            />
            Transfer to wallet
          </Button>
        </div>

        {/* Referred Users */}
        <div className="mt-6">
          <h3 className="text-dark-text-400 text-base font-medium">Referred users</h3>

          {/* Search */}
          <div
            className="mt-3 flex h-10 w-full items-center gap-2 rounded-full px-3"
            style={{
              background:
                "#eeeff8",
              border: "1px solid #D9D9D966",
            }}
          >
            <Search className="text-dark-text-300 size-4 shrink-0" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="min-w-0 flex-1 text-dark-text-400 border-0 bg-transparent text-base outline-none placeholder:text-dark-text-300"
            />
          </div>

          {/* User list */}
          <div className="mt-3 flex flex-col gap-2">
            {filtered.length === 0 ? (
              <p className="text-dark-text-300 py-6 text-center text-sm">
                No referred users found
              </p>
            ) : (
              filtered.map((r, i) => {
                const initials =
                  `${r.firstName[0]}${r.lastName[0]}`.toUpperCase();
                const isTopEarner = i === 0 && page === 0;
                return (
                  <div
                    key={r.id}
                    className="flex items-center gap-3 rounded-xl p-3"
                    style={{
                      background:
                        "#F5F7FB",
                      border: "1px solid #E1E5EB",
                    }}
                  >
                    {/* Avatar */}
                    <div
                      className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-bold"
                      style={{
                        background:
                          "#D5EAFF",
                        border: "1px solid #545A93",
                        color: "#4a93db",
                      }}
                    >
                      {initials}
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-dark-text-400">
                          {r.firstName} {r.lastName}
                        </span>
                        {isTopEarner && (
                          <span
                            className="shrink-0 rounded-full px-2 py-0.5 text-[8px] font-medium tracking-wide"
                            style={{
                              background:
                                "#D5EAFF",
                              color: "#4a93db",
                            }}
                          >
                            TOP EARNER
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[11px] font-light text-[#8D94AC]">
                        {r.email}
                        <span className="mx-1.5 text-sm opacity-50">•</span>
                        Joined {timeAgo(r.createdAt)}
                      </p>
                    </div>

                    {/* Earnings */}
                    <span
                      className="shrink-0 text-xs font-semibold"
                      style={{ color: "#4a93db" }}
                    >
                      {r.earnedAmount > 0
                        ? `+$${formatAmount(r.earnedAmount)}`
                        : "$0.00"}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="border-dark-stroke-3 bg-dark-grey-50 text-dark-text-300 hover:text-dark-text-400 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors disabled:opacity-30"
              >
                Prev
              </button>
              <span className="text-dark-text-300 text-xs">
                {page + 1} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="border-dark-stroke-3 bg-dark-grey-50 text-dark-text-300 hover:text-dark-text-400 rounded-full border px-4 py-1.5 text-xs font-medium transition-colors disabled:opacity-30"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* How it works */}
        <div
          className="mt-6 rounded-2xl border p-4"
          style={{
            background:
              "#F5F7FB",
            borderColor: "#E1E5EB",
          }}
        >
          <h3 className="text-base font-medium text-dark-text-400">How it works</h3>

          <div className="mt-4 flex flex-col">
            {[
              {
                n: 1,
                title: "Share your code",
                desc: "Send your referral code or link to anyone",
              },
              {
                n: 2,
                title: "They sign up & deposit",
                desc: "Friends create an account and fund their cards",
              },
              {
                n: 3,
                title: `You earn ${feePercent}%`,
                desc: `Get a ${feePercent === 25 ? "quarter" : `${feePercent}%`} of every deposit fee they pay`,
              },
            ].map((step, i, arr) => (
              <div key={step.n} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div
                    className="flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{
                      background:
                        "linear-gradient(135deg, #5a95d0 0%, #4a93db 100%)",
                    }}
                  >
                    {step.n}
                  </div>
                  {i < arr.length - 1 && (
                    <div className="my-1 w-px flex-1 bg-dark-stroke-3" />
                  )}
                </div>
                <div className="pb-4">
                  <p className="text-sm font-medium text-dark-text-400">{step.title}</p>
                  <p className="mt-0.5 text-xs font-light text-dark-text-300">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Share button — fixed to bottom of sheet */}
      <div className="sticky right-0 bottom-0 left-0 z-20 px-0 pt-3 pb-6">
        <button
          onClick={() => handleShare(`signup?ref=${referralCode}`)}
          className="flex w-full items-center justify-center gap-2 rounded-full py-2.5 text-sm font-semibold text-white transition-opacity active:opacity-80"
          style={{
            background: "linear-gradient(360deg, #5a95d0 0%, #4a93db 100%)",
          }}
        >
          <Share2 className="size-4" />
          Share Your Link
        </button>
        <p className="mt-2 text-center text-[11px] text-dark-text-300">
          Withdraw once you reach $100 &bull; Earnings credited within 24 hours
        </p>
      </div>
    </div>
  );
};

export default Referral;
